import * as React from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { cn } from '@/utils/cn';
import { ModalShell } from './ModalShell';

/** Props for `GradientModal`. `variant` switches the bordered-gradient palette. */
export interface GradientModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  variant?: 'purple' | 'gray';
  className?: string;
  hideCloseButton?: boolean;
  contentClassName?: string;
}

const PURPLE_INNER_GRADIENT =
  'linear-gradient(180deg, #EEEAFF -102.61%, rgba(255, 255, 255, 0) 140.84%)';
const GRAY_INNER_GRADIENT =
  'linear-gradient(180deg, #4A4A4A -102.61%, rgba(255, 255, 255, 0) 87.65%)';
const OUTER_BORDER_GRADIENT = 'linear-gradient(180deg, #1A1036 0%, #4B2E9C 100%)';

/** Centered modal with a bordered-gradient frame. Used by creator/editor flows. */
export const GradientModal: React.FC<GradientModalProps> = ({
  isOpen,
  onClose,
  children,
  variant = 'purple',
  className,
  hideCloseButton = false,
  contentClassName,
}) => {
  const innerGradient = variant === 'purple' ? PURPLE_INNER_GRADIENT : GRAY_INNER_GRADIENT;

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      position='center'
      // Terms-style modals hide the close button; suppress backdrop-click close too.
      closeOnBackdropClick={!hideCloseButton}
      bodyClassName={clsx(
        'flex max-h-[90vh] w-full flex-col',
        'max-w-creator-modal',
        'overflow-hidden rounded-20 shadow-2xl',
        className
      )}
      bodyStyle={{
        border: '4px solid transparent',
        backgroundClip: 'padding-box, padding-box, border-box',
        backgroundOrigin: 'padding-box, padding-box, border-box',
        backgroundImage: `${innerGradient}, linear-gradient(#FFFFFF, #FFFFFF), ${OUTER_BORDER_GRADIENT}`,
      }}
    >
      {!hideCloseButton && (
        <button
          onClick={onClose}
          className={clsx(
            'absolute right-4 top-4 z-10 rounded-full p-2 transition-colors',
            variant === 'purple'
              ? 'text-gray-500 hover:bg-black/5'
              : 'bg-red-50 text-red-400 hover:bg-red-100'
          )}
          aria-label='Close modal'
        >
          <XMarkIcon className='size-6' />
        </button>
      )}

      <div className={cn('global-scrollbar overflow-y-auto p-8 md:p-12', contentClassName)}>
        {children}
      </div>
    </ModalShell>
  );
};
